// bluaIQ — Contacts Linking Foundation (Phase 1, shared linker)
//
// One reusable surface to LINK an existing ProjectContact to a Contract,
// ContractorBid, or SupplierQuote — or to CREATE a contact (only on explicit
// confirmation) and link it. It NEVER creates a contact silently, NEVER creates
// a contract, and writes nothing downstream (no Budget / Payments / Calendar /
// Gantt / PCS / QuickBooks / Takeoff).
//
// Backed by the additive link endpoints:
//   PATCH /api/projects/:id/contracts/:contractId/contact   { contactId|null }
//   PATCH /api/projects/:id/bids/:bidId/contact             { contactId|null }
//   PATCH /api/projects/:id/quotes/:quoteId/contact         { contactId|null }
// and the existing explicit POST /api/projects/:id/contacts for create.

import { apiFetch } from '../utils/api'
import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { Link2, Link2Off, UserPlus, X, Check, Search } from 'lucide-react'

export interface LinkedContact {
  id: string
  name: string
  role?: string | null
  email?: string | null
  phone?: string | null
  company?: string | null
  trade?: string | null
}

interface ContactRow {
  id: string
  name: string
  role: string
  email?: string | null
  phone?: string | null
  company?: string | null
  trade?: string | null
}

export type LinkEntity = 'contract' | 'bid' | 'quote'

interface ContactLinkerProps {
  projectId: string
  entity: LinkEntity
  entityId: string
  /** The currently-linked contact, if any (from the record's include). */
  linkedContact?: LinkedContact | null
  /** Filter the picker + default create-role. CONTRACTOR for contracts/bids,
   *  SUPPLIER for quotes. Omit to show all roles. */
  roleFilter?: 'CONTRACTOR' | 'SUPPLIER'
  /** Optional detected info (name/email/phone) surfaced by extraction. Drives
   *  the "Contact detected" + prefilled Create panel. We only show what's given
   *  — never invented. */
  detected?: { name?: string | null; email?: string | null; phone?: string | null } | null
  isEs: boolean
  /** React-query keys to invalidate after a successful link/unlink/create. */
  invalidateKeys?: unknown[][]
  onChanged?: () => void
}

const T = (isEs: boolean) => ({
  contactDetected: isEs ? 'Contacto detectado' : 'Contact detected',
  createContact: isEs ? 'Crear contacto' : 'Create contact',
  linkExisting: isEs ? 'Enlazar a contacto existente' : 'Link to existing contact',
  ignore: isEs ? 'Ignorar' : 'Ignore',
  linkedContact: isEs ? 'Contacto enlazado' : 'Linked contact',
  notLinked: isEs ? 'No enlazado a un contacto' : 'Not linked to a contact',
  noContacts: isEs ? 'No hay contactos en este proyecto todavía.' : 'No contacts in this project yet.',
  search: isEs ? 'Buscar contacto…' : 'Search contact…',
  cancel: isEs ? 'Cancelar' : 'Cancel',
  confirmCreate: isEs ? 'Confirmar y crear' : 'Confirm & create',
  unlink: isEs ? 'Quitar enlace' : 'Remove link',
  name: isEs ? 'Nombre' : 'Name',
  email: isEs ? 'Correo' : 'Email',
  phone: isEs ? 'Teléfono' : 'Phone',
  supplier: isEs ? 'Proveedor' : 'Supplier',
  contractor: isEs ? 'Contratista' : 'Contractor',
  noWrites: isEs
    ? 'Esta acción no crea un contrato ni actualiza presupuesto, pagos o calendario/Gantt.'
    : 'This action does not create a contract or update Budget, Payments, or Calendar/Gantt.',
  failed: isEs ? 'No se pudo completar la acción.' : 'Could not complete the action.',
})

function linkPath(projectId: string, entity: LinkEntity, entityId: string): string {
  const seg = entity === 'contract' ? 'contracts' : entity === 'bid' ? 'bids' : 'quotes'
  return `/api/projects/${projectId}/${seg}/${entityId}/contact`
}

export default function ContactLinker({
  projectId,
  entity,
  entityId,
  linkedContact,
  roleFilter,
  detected,
  isEs,
  invalidateKeys = [],
  onChanged,
}: ContactLinkerProps) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const tx = T(isEs)

  const [mode, setMode] = useState<'idle' | 'pick' | 'create'>('idle')
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Local-only: user dismissed the detected-contact prompt for this session.
  // No schema, no endpoint — reloading shows the prompt again.
  const [ignored, setIgnored] = useState(false)
  // Prefill the create form from detected info (only what we actually have).
  const [draft, setDraft] = useState(() => ({
    name: detected?.name ?? '',
    email: detected?.email ?? '',
    phone: detected?.phone ?? '',
  }))

  const { data: contacts } = useQuery<ContactRow[]>({
    queryKey: ['contacts', projectId],
    enabled: mode === 'pick',
    queryFn: async () => {
      const token = await getToken()
      return apiFetch(`/api/projects/${projectId}/contacts`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      }).then((r) => (r.ok ? r.json() : []))
    },
  })

  const afterChange = () => {
    invalidateKeys.forEach((k) => queryClient.invalidateQueries({ queryKey: k }))
    queryClient.invalidateQueries({ queryKey: ['contacts', projectId] })
    onChanged?.()
  }

  // Link / unlink an existing contact (contactId null = unlink).
  const linkMutation = useMutation({
    mutationFn: async (contactId: string | null) => {
      const token = await getToken()
      const res = await apiFetch(linkPath(projectId, entity, entityId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ contactId }),
      })
      if (!res.ok) throw new Error(`link_failed_${res.status}`)
      return res.json()
    },
    onSuccess: () => { setMode('idle'); setError(null); afterChange() },
    onError: () => setError(tx.failed),
  })

  // Create a contact (explicit, confirmed) via the existing POST /contacts, then
  // link the new contact. NO silent creation: only runs from the Create panel's
  // Confirm button.
  const createMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      const body: Record<string, unknown> = {
        name: draft.name.trim(),
        role: roleFilter ?? 'OTHER',
      }
      if (draft.email.trim()) body.email = draft.email.trim()
      if (draft.phone.trim()) body.phone = draft.phone.trim()
      const res = await apiFetch(`/api/projects/${projectId}/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error(`create_failed_${res.status}`)
      const created = await res.json()
      await linkMutation.mutateAsync(created.id)
      return created
    },
    onSuccess: () => { setMode('idle'); setError(null); afterChange() },
    onError: () => setError(tx.failed),
  })

  const filtered = useMemo(() => {
    const rows = contacts ?? []
    const byRole = roleFilter ? rows.filter((c) => c.role === roleFilter) : rows
    const q = search.trim().toLowerCase()
    if (!q) return byRole
    return byRole.filter((c) =>
      [c.name, c.company, c.email, c.trade].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)),
    )
  }, [contacts, roleFilter, search])

  const roleWord = roleFilter === 'SUPPLIER' ? tx.supplier : roleFilter === 'CONTRACTOR' ? tx.contractor : ''
  const busy = linkMutation.isPending || createMutation.isPending

  return (
    <div data-testid="contact-linker" className="rounded-lg border border-[#ECEAE4] bg-[#FAF9F6] px-3 py-2 text-[12px]">
      {/* ── Linked state ─────────────────────────────────────────────────── */}
      {linkedContact ? (
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] font-[800] uppercase tracking-wide text-[#0E8C7F]">
              <Link2 size={12} /> {tx.linkedContact}
            </div>
            <div className="mt-0.5 font-[800] text-[#1C1A17] truncate">{linkedContact.name}</div>
            <div className="text-[11px] font-[600] text-[#6B655C] truncate">
              {[linkedContact.company, linkedContact.email, linkedContact.phone].filter(Boolean).join(' · ')}
            </div>
          </div>
          <button
            data-testid="contact-linker-unlink"
            disabled={busy}
            onClick={() => linkMutation.mutate(null)}
            className="flex items-center gap-1 shrink-0 rounded-full border border-[#ECEAE4] px-2.5 py-1 text-[10px] font-[800] text-[#9E9890] hover:bg-white disabled:opacity-40"
          >
            <Link2Off size={11} /> {tx.unlink}
          </button>
        </div>
      ) : (
        // ── Not-linked state ──────────────────────────────────────────────
        <div>
          <div className="flex items-center gap-1.5 text-[10px] font-[800] uppercase tracking-wide text-[#9E9890]">
            <Link2Off size={12} /> {tx.notLinked}
          </div>

          {/* Detected info (only what's available — never invented). */}
          {detected && (detected.name || detected.email || detected.phone) && mode === 'idle' && !ignored && (
            <div data-testid="contact-linker-detected" className="mt-1.5 rounded-md bg-white border border-[#ECEAE4] px-2.5 py-1.5">
              <div className="flex items-start justify-between gap-1">
                <div className="min-w-0">
                  <div className="text-[10px] font-[800] uppercase tracking-wide text-[#06539B]">{tx.contactDetected}</div>
                  <div className="text-[11px] font-[700] text-[#1C1A17] truncate">
                    {[detected.name, detected.email, detected.phone].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <button
                  data-testid="contact-linker-ignore"
                  onClick={() => setIgnored(true)}
                  className="shrink-0 pt-0.5 text-[10px] font-[800] text-[#9E9890] hover:text-[#1C1A17]"
                >
                  {tx.ignore}
                </button>
              </div>
            </div>
          )}

          {/* Idle actions */}
          {mode === 'idle' && (
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                data-testid="contact-linker-link-existing"
                onClick={() => { setMode('pick'); setError(null) }}
                className="flex items-center gap-1 rounded-full bg-[#06539B] px-3 py-1.5 text-[11px] font-[800] text-white hover:opacity-90"
              >
                <Link2 size={12} /> {tx.linkExisting}
              </button>
              <button
                data-testid="contact-linker-create"
                onClick={() => { setMode('create'); setError(null) }}
                className="flex items-center gap-1 rounded-full border border-[#ECEAE4] px-3 py-1.5 text-[11px] font-[800] text-[#1C1A17] hover:bg-white"
              >
                <UserPlus size={12} /> {tx.createContact}
              </button>
            </div>
          )}

          {/* Picker */}
          {mode === 'pick' && (
            <div className="mt-2">
              <div className="flex items-center gap-1.5 rounded-full border border-[#ECEAE4] bg-white px-2.5 py-1">
                <Search size={12} className="text-[#9E9890]" />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={tx.search}
                  className="w-full bg-transparent text-[12px] font-[600] focus:outline-none"
                />
                <button onClick={() => { setMode('idle'); setSearch('') }} className="text-[#9E9890]"><X size={13} /></button>
              </div>
              <div className="mt-1.5 max-h-44 overflow-auto rounded-md border border-[#ECEAE4] bg-white divide-y divide-[#F2F0EB]">
                {filtered.length === 0 && (
                  <div className="px-2.5 py-3 text-[11px] font-[600] text-[#9E9890]">{tx.noContacts}</div>
                )}
                {filtered.map((c) => (
                  <button
                    key={c.id}
                    data-testid="contact-linker-option"
                    disabled={busy}
                    onClick={() => linkMutation.mutate(c.id)}
                    className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left hover:bg-[#F2F0EB] disabled:opacity-40"
                  >
                    <span className="min-w-0">
                      <span className="block font-[800] text-[#1C1A17] truncate">{c.name}</span>
                      <span className="block text-[10px] font-[600] text-[#6B655C] truncate">
                        {[c.company, c.email, c.trade].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <Check size={13} className="shrink-0 text-[#0E8C7F]" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Create (explicit confirm — never silent) */}
          {mode === 'create' && (
            <div className="mt-2 space-y-1.5" data-testid="contact-linker-create-panel">
              {roleWord && (
                <div className="text-[10px] font-[800] uppercase tracking-wide text-[#06539B]">{roleWord}</div>
              )}
              <input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder={tx.name}
                className="w-full rounded-md border border-[#ECEAE4] bg-white px-2.5 py-1.5 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
              />
              <input
                value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                placeholder={tx.email}
                className="w-full rounded-md border border-[#ECEAE4] bg-white px-2.5 py-1.5 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
              />
              <input
                value={draft.phone}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                placeholder={tx.phone}
                className="w-full rounded-md border border-[#ECEAE4] bg-white px-2.5 py-1.5 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
              />
              <div className="flex gap-2 pt-0.5">
                <button onClick={() => setMode('idle')} className="flex-1 rounded-full border border-[#ECEAE4] py-1.5 text-[11px] font-[800] text-[#9E9890]">{tx.cancel}</button>
                <button
                  data-testid="contact-linker-create-confirm"
                  disabled={!draft.name.trim() || busy}
                  onClick={() => createMutation.mutate()}
                  className="flex-1 rounded-full bg-[#0E8C7F] py-1.5 text-[11px] font-[800] text-white disabled:opacity-40"
                >
                  {tx.confirmCreate}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {error && <div className="mt-1.5 text-[10px] font-[700] text-red-600">{error}</div>}

      {/* No-downstream-writes reassurance — always visible. */}
      <div className="mt-1.5 text-[9px] font-[600] italic text-[#9E9890]">{tx.noWrites}</div>
    </div>
  )
}
